import { DateTime } from 'luxon';
import createClient from '../create-client';
import { GetChainDetailsRequestParams } from './types/requests/GetChainDetailsRequestParams';
import { UnderlyingQuotesResponse } from './types/responses/UnderlyingQuote';
import {
  AccountBalance,
  DateZone,
  HistoricalBalance,
  OptionChain,
  OptionContract,
  Order,
  PortfolioDiffItem,
  PriceAlert,
  Strategy,
  StripeCustomer,
  UnderlyingQuote,
  UserInfo,
} from '@oai-frontend-monorepo/types';
import { OptionContractsResponse } from './types/responses/OptionContractsResponse';
import { QuoteResponse } from './types/responses/QuoteResponse';
import { ChainSummaryResponse } from './types/responses/ChainSummaryResponse';
import { ClosePositionParams } from './types/requests/ClosePositionParams';
import { GetOrdersParams } from './types/requests/GetOrdersParams';
import { RegisterUserParams } from './types/requests/RegisterUserParams';
import { RegisterUserResponse } from './types/responses/RegisterUserResponse';
import { GetActivationTokenResponse } from './types/responses/GetActivationTokenResponse';
import { OpenOrderParams } from './types/requests/OpenOrderParams';
import { TradierOpenOrderParams } from './types/requests/TradierOpenOrderParams';
import {
  mapOAIToTradier,
  OAISymbolParser,
  strategyIsCredit,
  symbolIsSPX,
  TradierSymbolParser,
} from '@oai-frontend-monorepo/utils';
import { GetChainByExpiryParams } from './types/requests/GetChainByExpiryParams';
import { GetNotificationsParams } from './types/requests/GetNotificationsParams';
import { GetNotificationsResponse } from './types/responses/GetNotificationsResponse';
import { PaperDepositResponse } from './types/responses/PaperDepositResponse';

export class OaiApiClient {
  public client: ReturnType<typeof createClient>;

  constructor(getToken?: () => Promise<string | undefined>) {
    this.client = createClient(``, getToken);
  }

  public async paperHistoricalBalance() {
    return await this.client.get<HistoricalBalance[]>(
      '/paper/balance/history?range=1000'
    );
  }

  public async cboeEntitlement() {
    await this.client.put<string>('/accounts/cboe', {});
    return 'success';
  }

  public async subscriptionsCustomer() {
    return this.client.get<StripeCustomer>(`/subscriptions/customer`);
  }

  public async checkVerificationCode(phoneNumber: string, code: string) {
    const res = await this.client.post<string>('/mobile/verify/complete', {
      phoneNumber,
      code,
    });

    return res || 'success';
  }

  public async getPhoneVerificationCode(phoneNumber: string) {
    const res = await this.client.post<string>('/mobile/verify/start', {
      phoneNumber,
    });

    return res;
  }

  public getPriceAlerts() {
    return this.client.get<PriceAlert[]>('/price-alerts');
  }

  public getPositionPriceAlerts() {
    return this.client.get<PriceAlert[]>('/position-price-alerts');
  }

  public async addPriceAlert(symbol: string, price: string) {
    await this.client.post('/price-alerts', {
      symbol,
      price,
    });

    return 'ok';
  }

  public async addPositionPriceAlert(
    positionId?: string,
    price?: string,
    condition?: string
  ) {
    await this.client.post('/position-price-alerts', {
      positionId,
      price,
      condition,
    });

    return 'success';
  }

  public async deletePriceAlert(id: string) {
    await this.client.delete(`/price-alerts`, {
      id,
    });

    return 'success';
  }

  public async deletePositionPriceAlert(id: string) {
    await this.client.delete(`/position-price-alerts`, {
      id,
    });

    return 'success';
  }

  public createStripeAccountSession() {
    return this.client.post<{ url?: string }>(
      '/subscriptions/create-portal-session',
      {}
    );
  }

  public setPaperBalance(amount = 10000) {
    return this.client.post<string>('/paper/balance/deposit', {
      targetBalance: amount,
    });
  }

  public getPaperDeposit() {
    return this.client.get<PaperDepositResponse>('/paper/balance/deposit');
  }

  public getPortfolioDifference() {
    return this.client.get<PortfolioDiffItem[]>(
      '/tradier/portfolio-difference'
    );
  }

  public async getPusherToken(): Promise<string | undefined> {
    const res = await this.client.get<{ token?: string }>('/pusher/beams-auth');

    return res?.token;
  }

  public async autheticateTradier(code: string, state: string) {
    const res = await this.client.get<string>(
      `/tradier/auth-exchange?code=${code}&state=${state}`
    );

    console.log('auth exchange res in client', res);

    return res || 'success';
  }

  public async info() {
    return this.client.get<UserInfo>('/account/info');
  }

  public async removeFromWatchlist(symbol: string) {
    return this.client.delete(`/watchlists/${symbol}`);
  }

  public async getActivationToken(email: string) {
    return this.client.get<GetActivationTokenResponse>(
      `/users?email=${encodeURIComponent(email)}`
    );
  }

  public async registerUser(params: RegisterUserParams) {
    const res = await this.client.post<RegisterUserResponse>(
      '/registrations',
      params
    );

    // if (res.status === 201) {
    //   return this.getActivationToken(params.email);
    // }

    return res as RegisterUserResponse;
  }

  public async getOrder(orderId: string) {
    const orders = await this.client.get<Order[]>(
      `/tradier/orders?page=${1}&size=${40}&working=false`
    );

    const ordersWorking = await this.client.get<Order[]>(
      `/tradier/orders?page=${1}&size=${20}&working=true`
    );

    const allOrders = [...orders, ...ordersWorking];

    return allOrders.find((order) => order.orderId === orderId);
  }

  public async getOrdersById(orderIds: string[]) {
    const historicOrders = await this.client.get<Order[]>(
      `/tradier/orders?page=${1}&size=${40}&working=false`
    );

    const ordersWorking = await this.client.get<Order[]>(
      `/tradier/orders?page=${1}&size=${20}&working=true`
    );

    const orders = [...historicOrders, ...ordersWorking];

    return orders.filter((order) => orderIds.includes(order.orderId));
  }

  public async getOrders({
    status,
    size,
    page,
    includeConfirms,
    working,
  }: GetOrdersParams) {
    return this.client.get<Order[]>(
      `/tradier/orders?page=${page}&size=${size}&working=${working}`
    );
  }

  public async previewOpenOrder({
    legs,
    limitPrice,
    positionType,
    quantity,
    strategy,
    duration,
  }: OpenOrderParams) {
    if (positionType !== 'LIVE') {
      return 'success';
    }

    if (!legs?.length || !limitPrice || !strategy || !duration || !quantity) {
      throw new Error('Missing parameters');
    }

    const IS_STOCK = strategy === Strategy.LONG_STOCK;

    const body: TradierOpenOrderParams = {
      legs: IS_STOCK
        ? undefined
        : legs.map((leg) => ({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            symbol: mapOAIToTradier(leg.symbol)!,
            side: leg.side === 'SELL' ? 'sell_to_open' : 'buy_to_open',
          })),
      side: IS_STOCK ? 'buy' : undefined,
      orderClass: IS_STOCK ? 'equity' : legs.length > 1 ? 'multileg' : 'option',
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rootSymbol: new OAISymbolParser(legs[0].symbol).underlyingSymbol!,
      type:
        legs.length > 1
          ? strategyIsCredit(strategy)
            ? 'credit'
            : 'debit'
          : 'limit',
      duration,
      quantity: quantity.toString(),
      preview: true,
      price: limitPrice,
    };

    const res = await this.client.post<string>('/tradier/orders', body);

    console.log('new order - previewOpenOrder - res in client', res);

    return res;
  }

  public async cancelOrder(orderId: string) {
    return this.client.delete<string>(`/tradier/orders/${orderId}`);
  }

  public async submitOpenOrder({
    legs,
    limitPrice,
    positionType,
    quantity,
    strategy,
    duration,
  }: OpenOrderParams) {
    if (!legs?.length || !limitPrice || !strategy || !duration || !quantity) {
      throw new Error('Missing parameters');
    }

    const IS_STOCK = strategy === Strategy.LONG_STOCK;

    const body: TradierOpenOrderParams = {
      legs: IS_STOCK
        ? undefined
        : legs.map((leg) => ({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            symbol: mapOAIToTradier(leg.symbol)!,
            side: leg.side === 'SELL' ? 'sell_to_open' : 'buy_to_open',
          })),
      orderClass: IS_STOCK ? 'equity' : legs.length > 1 ? 'multileg' : 'option',
      side: IS_STOCK ? 'buy' : undefined,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rootSymbol: new OAISymbolParser(legs[0].symbol).underlyingSymbol!,
      type:
        legs.length > 1
          ? strategyIsCredit(strategy)
            ? 'credit'
            : 'debit'
          : 'limit',
      duration,
      quantity: quantity.toString(),
      preview: false,
      price: limitPrice,
    };

    console.log('order submit', body);

    const res = await this.client.post<string>('/tradier/orders', body);

    return res;
  }

  public async submitClosePosition({
    limitPrice,
    quantity,
    strategy,
    legs,
    positionType,
    positionId,
    duration,
  }: ClosePositionParams) {
    const rootSymbol = new TradierSymbolParser(legs[0].symbol).parsed
      ?.underlyingSymbol;

    if (!rootSymbol) {
      throw new Error('Root symbol not found');
    }

    const isStock = strategy === Strategy.LONG_STOCK;

    const body: TradierOpenOrderParams = {
      legs: isStock
        ? undefined
        : legs.map((leg) => ({
            symbol: leg.symbol,
            side: leg.side === 'SELL' ? 'sell_to_close' : 'buy_to_close',
          })),
      orderClass: isStock ? 'equity' : legs.length > 1 ? 'multileg' : 'option',
      side: isStock ? 'sell' : undefined,
      rootSymbol,
      type:
        legs.length > 1
          ? strategyIsCredit(strategy)
            ? 'debit'
            : 'credit'
          : 'limit',
      duration,
      quantity: quantity.toString(),
      preview: false,
      price: limitPrice,
      targetPositionId: positionId,
    };

    const res = await this.client.post<string | number>(
      `/tradier/orders`,
      body
    );

    console.log('submitsuccess in method res', res);

    return res;
  }

  public async previewClosePosition({
    limitPrice,
    quantity,
    strategy,
    legs,
    positionType,
    positionId,
    duration,
  }: ClosePositionParams) {
    if (positionType !== 'LIVE') {
      return 'success';
    }

    const rootSymbol = new TradierSymbolParser(legs[0].symbol).parsed
      ?.underlyingSymbol;

    if (!rootSymbol) {
      throw new Error('Root symbol not found');
    }

    const isStock = strategy === Strategy.LONG_STOCK;

    const body: TradierOpenOrderParams = {
      legs: isStock
        ? undefined
        : legs.map((leg) => ({
            symbol: leg.symbol,
            side: leg.side === 'SELL' ? 'sell_to_close' : 'buy_to_close',
          })),
      side: isStock ? 'sell' : undefined,
      orderClass: isStock ? 'equity' : legs.length > 1 ? 'multileg' : 'option',
      rootSymbol,
      type:
        legs.length > 1
          ? strategyIsCredit(strategy)
            ? 'debit'
            : 'credit'
          : 'limit',
      duration,
      quantity: quantity.toString(),
      preview: true,
      price: limitPrice,
      targetPositionId: positionId,
    };

    const res = await this.client.post<string>(`/tradier/orders`, body);

    return res;
  }

  public async getBalance() {
    return this.client.get<AccountBalance>(`/tradier/balance`);
  }

  public async getUnderlyingQuote(symbol: string) {
    const usedSymbol = symbolIsSPX(symbol) ? '^SPX' : symbol;

    const response = await this.client.get<UnderlyingQuotesResponse>(
      `/markets/tradier/quotes/${encodeURI(usedSymbol)}`
    );

    return response.quotes.quote;
  }

  async getOptionContracts(symbols: string[]): Promise<OptionContract[]> {
    const res = await this.client.get<OptionContractsResponse>(
      `/markets/tradier/quotes/${symbols.join(',')}`
    );

    if (!res.quotes.quote) {
      return [];
    }

    return Array.isArray(res.quotes.quote)
      ? res.quotes.quote
      : [res.quotes.quote];
  }

  async getWatchlists() {
    return this.client.get<string[]>('/watchlists');
  }

  async addToWatchlist(symbol: string) {
    return this.client.post<string>('/watchlists', { symbol });
  }

  async getQuotes(
    symbols: string[]
  ): Promise<(UnderlyingQuote | OptionContract)[]> {
    const usedSymbols = symbols.map((s) => {
      if (s.length > 6) {
        return s.replace('SPX', 'SPXW').replace('SPXWW', 'SPXW');
      }

      if (symbolIsSPX(s) && s.length <= 6) {
        return encodeURI('^SPX');
      }

      return s;
    });

    const res = await this.client.get<QuoteResponse>(
      `/markets/v1/quotes/${usedSymbols.join(',')}`
    );

    if (!res) {
      return [] as (UnderlyingQuote | OptionContract)[];
    }

    return (res as (UnderlyingQuote | OptionContract)[]).map((q) => {
      return q;
    });
  }

  public async getChainSummary(symbol: string) {
    const res = await this.client.get<ChainSummaryResponse>(
      `/markets/tradier/expire-strikes?symbol=${symbol}`
    );

    const oneYear = DateTime.utc()
      .setZone(DateZone.New_York)
      .plus({ day: 365 });

    const now = DateTime.utc().setZone(DateZone.New_York);

    return res?.expirations.expiration?.filter((d) => {
      const date = DateTime.fromFormat(d.date, 'yyyy-MM-dd', {
        zone: DateZone.New_York,
      }).plus({ hour: 16 });

      return date <= oneYear && date >= now;
    });
  }

  public async getChainDetails({
    symbol,
    minExpire = DateTime.utc()
      .setZone(DateZone.New_York)
      .minus({ day: 1 })
      .toFormat('yyMMdd'),
    maxExpire,
    maxStrikeDistance = 1,
    atm,
  }: GetChainDetailsRequestParams) {
    const res = await this.client.get<OptionChain[]>(
      `/markets/tradier/chain-details?symbol=${symbol}&minExpire=${minExpire}&maxExpire=${maxExpire}&maxStrikeDistance=${maxStrikeDistance}&atm=${atm}`
    );

    return res
      .sort(
        (a, b) =>
          DateTime.fromFormat(a.expire, 'yyMMdd', {
            zone: DateZone.New_York,
          }).toMillis() -
          DateTime.fromFormat(b.expire, 'yyMMdd', {
            zone: DateZone.New_York,
          }).toMillis()
      )
      .map((chain) => {
        const expire = DateTime.fromFormat(chain.expire, 'yyMMdd', {
          zone: DateZone.New_York,
        });

        return {
          expire: expire.set({ hour: 16 }).toFormat('yyyy-MM-dd HH:mm:ss'),
          contracts: chain.contracts,
        };
      });
  }

  public async getChainByExpiry({
    symbol,
    expiry,
    maxStrikeDistance = 500,
    atm,
  }: GetChainByExpiryParams) {
    const formatedExpiry = DateTime.fromFormat(expiry, 'yyyy-MM-dd', {
      zone: DateZone.New_York,
    });

    const res = await this.client.get<OptionChain[]>(
      `/markets/tradier/chain-details?symbol=${symbol}&minExpire=${formatedExpiry
        .minus({ day: 1 })
        .toFormat('yyMMdd')}&maxExpire=${formatedExpiry.toFormat(
        'yyMMdd'
      )}&maxStrikeDistance=${maxStrikeDistance}&atm=${atm}`
    );

    return res
      .sort(
        (a, b) =>
          DateTime.fromFormat(a.expire, 'yyMMdd', {
            zone: DateZone.New_York,
          }).toMillis() -
          DateTime.fromFormat(b.expire, 'yyMMdd', {
            zone: DateZone.New_York,
          }).toMillis()
      )
      .map((chain) => {
        const expire = DateTime.fromFormat(chain.expire, 'yyMMdd', {
          zone: DateZone.New_York,
        });

        return {
          expire: expire.set({ hour: 16 }).toFormat('yyyy-MM-dd HH:mm:ss'),
          contracts: chain.contracts,
        };
      });
  }

  public registerGetToken(
    baseUri: string,
    getToken?: () => Promise<string | undefined>
  ) {
    this.client = createClient(baseUri, getToken);

    return this;
  }

  public getNotifications({
    limit,
    page,
    unread = true,
  }: GetNotificationsParams): Promise<GetNotificationsResponse> {
    return this.client.get(
      `/notifications?limit=${limit}&page=${page}&countUnread=${unread}`
    );
  }

  public async markNotificationAsRead(notificationId: string): Promise<string> {
    await this.client.patch(`/notifications/${notificationId}`, {});

    return 'success';
  }

  public async markAllNotificationsAsRead(): Promise<string> {
    await this.client.patch(`/notifications`, {});

    return 'success';
  }

  public async togglePaperTrading(): Promise<string> {
    const res = await this.client.post<string>('/accounts/toggle-type', {});

    return res;
  }
}

export default new OaiApiClient();
